namespace Solovis.UI {

    export interface DialogOptions {
        container: any;

        width?: any;
        minWidth?: any;
        maxWidth?: any;

        height?: any;
        minHeight?: any;
        maxHeight?: any;

        title?: string | boolean;
        modal?: boolean;
        pinned?: boolean;
        autoOpen?: boolean;
        center?: boolean;
        position?: { top?: number; left?: number };
        destroyOnClose?: boolean;
        actions?: string[];
        actionHandlers?: any;
        noAnimation?: boolean;
        noPaddingOnContent?: boolean;
        noOverflowScroll?: boolean;
        ownerDialog?: Solovis.UI.Dialog;
        buttons?: DialogButtonOptions[];

        open?: () => any;
        activate?: () => any;
        deactivate?: () => any;
        close?: () => any;
        refresh?: () => any;
        resize?: () => any;

        /**
         * If set the dialog will try to size the this to the content window area.
         */
        contentWidthSizer?: ISupportWidthAutoSize;
        /**
         * If set the dialog will try to size the this to the content window area.
         */
        contentHeightSizer?: ISupportHeightAutoSize;
    }

    export interface DialogButtonOptions {
        label?: string;
        click?: any;
        visible?: boolean;
    }

    export interface ISupportWidthAutoSize {
        setWidth: (newWidth: number) => void;
    }

    export interface ISupportHeightAutoSize {
        setHeight: (newHeight: number) => void;
    }

    export class Dialog {

        public options: DialogOptions;
        public _contentWrapper: any;
        public _buttonRowContainer: any;
        public _windowContainer: any;
        public window: any;
        public _windowWrapper: any;
        public _ownerDialogHandler: any;
        public _ownerDialogClosingHandler: any;

        public _widthAutoSizer: ISupportWidthAutoSize;
        public _heightAutoSizer: ISupportHeightAutoSize;
        
        /*         
         * Events fired by Solovis.UI.Dialog:
         *      dialogClosing
         *      dialogDeactivated
         * 
         */
        constructor(options: DialogOptions) {
            var self = this;

            this.options = options;

            if (!options.container) {
                throw "options.container not specified";
            }

            var windowOptions: any = {};

            var copyOption = function (optionName) {
                if (Solovis.isNotNull(options[optionName])) {
                    windowOptions[optionName] = options[optionName];
                }
            };

            if (Solovis.isNotNull(options.title)) {
                if (typeof (options.title) === "string") {
                    // Escape the title since Kendo window does not do that.
                    windowOptions.title = $("<span>").text(options.title).html();
                }
            }

            copyOption("width");
            copyOption("minWidth");
            copyOption("maxWidth");

            copyOption("height");
            copyOption("minHeight");
            copyOption("maxHeight");

            copyOption("modal");
            copyOption("pinned");

            copyOption("position");

            if (Solovis.isNotNull(this.options.actions)) {
                windowOptions.actions = this.options.actions;
            }
            else {
                windowOptions.actions = {};
            }

            windowOptions.open = function () { self._handleOpen(); };
            windowOptions.activate = function () { self._handleActivate(); };
            windowOptions.deactivate = function () { self._handleDeactivate(); };
            windowOptions.close = function () { self._handleClose(); };
            windowOptions.refresh = function () { self._handleRefresh(); };
            windowOptions.resize = function () { self._handleResize(); };

            var windowContainer = null;
            if (options.buttons) {
                // Wrap the container so that we can add a row of buttons.
                windowContainer = $("<div>")
                    .addClass("solovis-dialog")
                    .addClass("overflow-hidden")
                    .css("position", "relative")
                    .css("padding", "0");

                // contentWrapper provides auto scrolling just for the desired content
                var contentWrapper = $("<div>").addClass("dialog-content-wrapper-for-button-row")
                    .appendTo(windowContainer);

                if (options.noPaddingOnContent) {
                    contentWrapper.css("padding", "0");
                }
                if (options.noOverflowScroll) {
                    contentWrapper.css("overflow", "hidden");
                }

                contentWrapper.append(this.options.container);

                this._contentWrapper = contentWrapper;

                this._buttonRowContainer = this._createButtonRowContainer(options.buttons);
                windowContainer.append(this._buttonRowContainer);
            }
            else {
                // Directly use the window container.
                windowContainer = this.options.container;

                if (options.noPaddingOnContent) {
                    windowContainer.css("padding", "0");
                }
                if (options.noOverflowScroll) {
                    windowContainer.css("overflow", "hidden");
                }
            }

            this._windowContainer = windowContainer;

            if (options.noAnimation) {
                windowOptions.animation = false;
            }

            this.window = ($(windowContainer) as any).kendoWindowForUIDialog(windowOptions);
            this._windowWrapper = this.window.data("kendoWindowForUIDialog").wrapper;

            var kWindowElement = $(windowContainer).closest(".k-window").get(0);
            (kWindowElement as any).__solovisDialog = this;

            var $kWindowElement = $(kWindowElement);
            $kWindowElement.addClass("solovis-dialog");

            var applyBorderBoxFix = function () {
                // NOTE: Made this a function to highlight that the only reason we are doing this section of code
                // is to make it so that our dialog contents maintain our standard border-box sizing.

                // NOTE: We have to remove k-widget from dialog to prevent Kendo's content-box box-sizing from overriding our border-box preference
                //   on dialog content. Hopefully, Kendo does not introduce changes in future that require k-widget on that element.                
                $kWindowElement.removeClass("k-widget");
            };
            applyBorderBoxFix();

            // Register handlers for custom actions.
            if (this.options.actionHandlers) {
                for (var actionName in this.options.actionHandlers) {
                    var actionHandler = this.options.actionHandlers[actionName];
                    var actionNameLower = actionName.toLowerCase();
                    var iconElement = this._windowWrapper.find(".k-i-" + actionNameLower);
                    iconElement.click(function () {
                        actionHandler.call(self);
                    });
                }
            }

            if (this.options.center) {
                this.center();
            }

            if (this.options.autoOpen) {
                this.open();
            }
        }

        private _fixupDevExpressZIndex() {
            if ((window as any).DevExpress) {
                // Devextreme does not dynamically calculate z-index numbers so we have to provide
                // this workaround so that their popups appear above our dialogs.
                let zIndexBase = Math.max(2000, Solovis.UI.DomHelper.findMaxZIndexForElementChildren(document.body) + 100);
                (window as any).DevExpress.ui.dxOverlay.baseZIndex(zIndexBase);
            }
        }


        public getWindowElement() {
            /// <summary>
            /// Return the DOM element containing the window.
            /// </summary>
            return this._windowWrapper.get(0);
        }

        public getButtonByLabel(labelText) {
            var buttonToReturn = null;
            if (this._buttonRowContainer) {
                this._buttonRowContainer.find("button").each(function (i, button) {
                    button = $(button);
                    var buttonText = button.text();
                    if (buttonText === labelText) {
                        buttonToReturn = button;
                    }
                });
            }
            return buttonToReturn;
        }

        public getAvailableContentHeight() {
            var windowWrapperHeight = $(this._windowWrapper).height();
            var buttonContainerHeight = this._buttonRowContainer ? $(this._buttonRowContainer).height() : 0;

            var availableContentHeight = windowWrapperHeight - buttonContainerHeight - 1;
            if (!this.options.noPaddingOnContent) {
                // Following line will need to be adjusted based on dialog style.
                availableContentHeight -= 24;
            }

            return availableContentHeight;
        }

        public getAvailableContentWidth() {
            let windowWrapperWidth = $(this._windowWrapper).width();
            let availableContentWidth = windowWrapperWidth;
            if (!this.options.noPaddingOnContent) {
                // Following line will need to be adjusted based on dialog style.
                // Subtract out space for vertical scrollbar and padding.
                availableContentWidth -= (20 + 12);
            }

            return availableContentWidth;
        }



        public _createButtonRowContainer = function (buttonList) {
            var buttonRowContainer = $("<div>").addClass("dialog-button-row-container");
            this._updateButtonRowContainer(buttonRowContainer, buttonList);

            return buttonRowContainer;
        }

        private _updateButtonRowContainer(buttonRowContainer: JQuery, buttonList: Array<DialogButtonOptions>) {
            buttonRowContainer.html("");

            const buttonRowDiv = $("<div>").addClass("dialog-button-row").appendTo(buttonRowContainer);

            $(buttonList).each(function (i, buttonInfo: Element & DialogButtonOptions) {
                const button = $("<button>").text(buttonInfo.label);
                button.attr("data-button-title", buttonInfo.label);

                if (buttonInfo.click) {
                    button.click(buttonInfo.click);
                }

                if (Solovis.isNotNull(buttonInfo.visible)) {
                    if (!buttonInfo.visible) {
                        button.css("display", "none");
                    }
                }

                buttonRowDiv.append(button);
            });
        }

        public open() {
            this.window.data("kendoWindowForUIDialog").open();

            this._fixupDevExpressZIndex();
        }

        public maximize() {
            this.window.data("kendoWindowForUIDialog").maximize();
            this._syncAutoSizers();
        }

        public minimize() {
            this.window.data("kendoWindowForUIDialog").minimize();
        }

        public restore() {
            this.window.data("kendoWindowForUIDialog").restore();
        }

        public center() {
            this.window.data("kendoWindowForUIDialog").center();
        }

        public toFront() {
            this.window.data("kendoWindowForUIDialog").toFront();

            this._fixupDevExpressZIndex();
        }

        public pin() {
            if (this.window) {
                this.window.data("kendoWindowForUIDialog").pin();
            }
        }

        public unpin() {
            if (this.window) {
                this.window.data("kendoWindowForUIDialog").unpin();
            }
        }

        public close() {
            if (this.window) {
                this.window.data("kendoWindowForUIDialog").close();
            }
        }

        public destroy() {
            if (this.window) {
                this.window.data("kendoWindowForUIDialog").destroy();
                this.window = null;
            }
        }

        public title(newTitle?) {
            // Will return title if newTitle not supplied.
            if (Solovis.isNotNull(newTitle)) {
                // Escape the title since Kendo window does not do that.
                let escapedNewTitle = $("<span>").text(newTitle).html();

                this.window.data("kendoWindowForUIDialog").title(escapedNewTitle);
            }
            else {
                return this.window.data("kendoWindowForUIDialog").title();
            }
        }

        public position(newPosition?) {
            // Will return position if newPosition not supplied.
            if (Solovis.isNotNull(newPosition)) {
                this.window.data("kendoWindowForUIDialog").wrapper.offset(newPosition);
            }
            else {
                return this.window.data("kendoWindowForUIDialog").wrapper.offset();
            }
        }

        public updateButtons(buttonList) {
            if (this._buttonRowContainer) {
                this._updateButtonRowContainer(this._buttonRowContainer, buttonList);
            }
            else {
                throw "Button row container does not exist for this dialog.";
            }
        }

        public width(newWidth?) {
            // Will return width if newWidth not supplied.
            if (Solovis.isNotNull(newWidth)) {
                this.window.data("kendoWindowForUIDialog").wrapper.width(newWidth);
            }
            else {
                return this.window.data("kendoWindowForUIDialog").wrapper.width();
            }
        }

        public height(newHeight?) {
            // Will return width if newHeight not supplied.
            if (Solovis.isNotNull(newHeight)) {
                this.window.data("kendoWindowForUIDialog").wrapper.height(newHeight);
            }
            else {
                return this.window.data("kendoWindowForUIDialog").wrapper.height();
            }
        }

        public syncAutoSizers() {
            this._syncAutoSizers();
        }

        private _syncAutoSizers() {
            if (this.options.contentWidthSizer && this.options.contentWidthSizer.setWidth) {
                let width = this.getAvailableContentWidth();
                this.options.contentWidthSizer.setWidth(width);
            }

            if (this.options.contentHeightSizer && this.options.contentHeightSizer.setHeight) {
                let height = this.getAvailableContentHeight();
                this.options.contentHeightSizer.setHeight(height);
            }
        }

        public _handleOpen() {
            if (this.options.open) {
                this.options.open.call(this);
            }

            if (this.options.ownerDialog) {
                this._registerOwnerDialogHandler();
            }
        }

        public _handleActivate() {
            this._syncAutoSizers();

            if (this.options.activate) {
                this.options.activate.call(this);
            }

            // In some cases, this does not get called after window is at final size, so we do a follow up size shortly
            // thereafter as a workaround.
            setTimeout(() => this._syncAutoSizers(), 100);
        }

        public _handleClose() {
            if (this.options.close) {
                this.options.close.call(this);
            }

            $(this).trigger("dialogClosing");
        }

        public _handleDeactivate() {
            this._unregisterOwnerDialogHandler();

            if (this.options.deactivate) {
                this.options.deactivate.call(this);
            }

            if (this.options.destroyOnClose) {
                this.destroy();
            }

            if ((document.body as any).__DockPanelControlDisabledScroll) {
                // Maximized dialogs mess up the disabled scrollbar, so we have to reapply this.
                $(document.body).css("overflow", "hidden");
            }

            $(this).trigger("dialogDeactivated");
        }

        public _handleRefresh() {
            if (this.options.refresh) {
                this.options.refresh.call(this);
            }
        }

        public _handleResize() {
            if (this.options.resize) {
                this.options.resize.call(this);
            }

            this._syncAutoSizers();
        }

        public autoAdjustToViewport() {
            /// <summary>
            /// Move dialog into viewport range. Don't allow window size to exceed viewport size.
            /// </summary>
            var width = this.width();
            var height = this.height();
            var offset = this.position();

            var adjusted = Solovis.UI.Dialog.autoAdjustSizeAndPosToViewport(offset, { width: width, height: height });
            if (adjusted.left != offset.left || adjusted.top != offset.top) {
                this.position({ left: adjusted.left, top: adjusted.top });
            }

            if (adjusted.width != width) {
                this.width(adjusted.width);
            }

            if (adjusted.height != height) {
                this.height(adjusted.height);
            }
        }

        public static autoAdjustSizeAndPosToViewport(offset, assumedSize) {
            /// <summary>
            /// A static helper method that can help adjust proposed dialog position
            /// to the current viewport.
            /// </summary>

            var windowLeft = $(window).scrollLeft();
            var windowTop = $(window).scrollTop();
            var windowWidth = $(window).width();
            var windowHeight = $(window).height();
            var windowRight = windowLeft + windowWidth;
            var windowBottom = windowTop + windowHeight;

            var adjusted = {
                left: offset.left,
                top: offset.top,
                width: assumedSize ? assumedSize.width : 0,
                height: assumedSize ? assumedSize.height : 0
            };

            // Ensure we aren't off left/top edge.
            if (adjusted.left < windowLeft) {
                adjusted.left = windowLeft;
            }
            if (adjusted.top < windowTop) {
                adjusted.top = windowTop;
            }

            // Ensure we aren't off right/bottom edge.
            if ((adjusted.left + adjusted.width) > windowRight) {
                adjusted.left = windowRight - adjusted.width;
            }
            if ((adjusted.top + adjusted.height) > windowBottom) {
                adjusted.top = windowBottom - adjusted.height;
            }

            // Resize if we are now pushed back past left edge.
            if (adjusted.left < windowLeft) {
                adjusted.left = windowLeft;
                adjusted.width = windowWidth;
            }
            if (adjusted.top < windowTop) {
                adjusted.top = windowTop;
                adjusted.height = windowHeight;
            }

            return adjusted;
        }

        public windowZIndex(zIndex?) {
            var jDialog = $(this._windowContainer).closest(".k-window");
            if (!arguments.length) {
                var currentZIndex = jDialog.css("z-index");
                if (currentZIndex) {
                    return parseInt(currentZIndex);
                }
                else {
                    return 0;
                }
            }
            else {
                jDialog.css("z-index", zIndex);
            }
        }

        public _registerOwnerDialogHandler() {
            var self = this;

            if (this._ownerDialogHandler) {
                return;
            }

            this._ownerDialogHandler = function () {
                var ownerZIndex = self.options.ownerDialog.windowZIndex();
                var currentZIndex = self.windowZIndex();
                var desiredZIndex = ownerZIndex + 1;
                if (currentZIndex < desiredZIndex) {
                    self.windowZIndex(desiredZIndex);
                }
            };

            window.addEventListener('blur', this._ownerDialogHandler, true);
            window.addEventListener('focus', this._ownerDialogHandler, true);
            window.addEventListener('mouseup', this._ownerDialogHandler, true);

            this._ownerDialogClosingHandler = function () {
                self.close();
            };

            $(this.options.ownerDialog).bind("dialogClosing", this._ownerDialogClosingHandler);
        }

        public _unregisterOwnerDialogHandler() {
            if (this._ownerDialogHandler) {
                window.removeEventListener('blur', this._ownerDialogHandler, true);
                window.removeEventListener('focus', this._ownerDialogHandler, true);
                window.removeEventListener('mouseup', this._ownerDialogHandler, true);

                this._ownerDialogHandler = null;
            }

            if (this._ownerDialogClosingHandler) {
                $(this.options.ownerDialog).unbind("dialogClosing", this._ownerDialogClosingHandler);

                this._ownerDialogClosingHandler = null;
            }
        }

        public static findContainingDialog(element) {
            /// <summary>
            /// findContainingDialog is a static dialog that will return the Solovis.UI.Dialog containing the element (or null, if none).
            /// </summary>
            var jDialog = $(element).closest(".k-window");
            if (jDialog.length > 0) {
                var dialogElement = jDialog.get(0);
                if (dialogElement && (dialogElement as any).__solovisDialog) {
                    return (dialogElement as any).__solovisDialog;
                }
            }

            return null;
        }

        public static findDialogByTitle(title) {
            var dialogFound = null;

            $(".solovis-dialog").each(function (i, dialogElement: any) {
                var dialog = dialogElement.__solovisDialog;
                if (dialog) {
                    if (dialog.title() == title) {
                        if (!dialogFound) {
                            dialogFound = dialog;
                        }
                        else {
                            throw "Multiple dialogs have same title: " + title;
                        }
                    }
                }
            });

            return dialogFound;
        }

        public static findTopLevelDialog = function () {
            var topLevelDialog = null;
            var zIndex = -1;

            $(".solovis-dialog").each(function (i, dialogElement: any) {
                var dialog = dialogElement.__solovisDialog;
                if (dialog) {
                    var dialogZIndex = parseInt($(dialogElement).css("z-index"));
                    if (dialogZIndex > zIndex) {
                        topLevelDialog = dialog;
                        zIndex = dialogZIndex;
                    }
                }
            });

            return topLevelDialog;
        }
    }

}